import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { saveUiPreferences } from '../../services/settingsAPI';
import { applyUiPreferences } from '../../services/uiPreferencesBridge';
import {
  SETTINGS_FORM_GRID,
  SETTINGS_FIELD,
  SETTINGS_LABEL,
  SETTINGS_INPUT,
  SETTINGS_SELECT,
  SETTINGS_SECTION,
  SETTINGS_PAGE_STACK,
} from '../../utils/settingsUi';
import {
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  CurrencyDollarIcon,
  LanguageIcon,
  ClockIcon,
  PhotoIcon,
  CloudArrowUpIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const GeneralSettings = ({ settings, onSettingsChange, onHasChanges }) => {
  const [formData, setFormData] = useState({
    gymName: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    currency: 'DA',
    language: 'fr',
    timezone: 'Africa/Algiers',
    logo: null
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (settings) {
      setFormData({
        gymName: settings.gymName || '',
        address: settings.address || '',
        phone: settings.phone || '',
        email: settings.email || '',
        website: settings.website || '',
        currency: settings.currency || 'DA',
        language: settings.language || 'fr',
        timezone: settings.timezone || 'Africa/Algiers',
        logo: settings.logo || null
      });
      // Set logo preview if logo exists
      if (settings.logo) {
        setLogoPreview(settings.logo);
      }
    }
  }, [settings]);

  const handleChange = async (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);

    if (typeof onSettingsChange === 'function') {
      onSettingsChange({ ...settings, ...newFormData });
    }

    if (field === 'language') {
      try {
        await saveUiPreferences({ language: value });
        applyUiPreferences({ language: value });
      } catch {
        /* LanguageContext keeps local state */
      }
    }

    if (typeof onHasChanges === 'function') {
      onHasChanges(true);
    }
  };

  // Handle logo upload
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      setUploadStatus('error');
      toast.error('Veuillez sélectionner un fichier image (PNG, JPG, etc.)');
      setTimeout(() => setUploadStatus('idle'), 3000);
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadStatus('error');
      toast.error('Le fichier est trop volumineux (max. 5MB)');
      setTimeout(() => setUploadStatus('idle'), 3000);
      return;
    }

    setUploadStatus('uploading');

    const reader = new FileReader();
    reader.onload = (e) => {
      const logoData = e.target.result;
      setLogoPreview(logoData);
      handleChange('logo', logoData);
      setUploadStatus('success');
      toast.success('Logo téléchargé avec succès!');
      setTimeout(() => setUploadStatus('idle'), 3000);
    };

    reader.onerror = () => {
      setUploadStatus('error');
      toast.error('Erreur lors du téléchargement du fichier');
      setTimeout(() => setUploadStatus('idle'), 3000);
    };

    reader.readAsDataURL(file);
  };

  // Remove logo
  const handleLogoRemove = () => {
    setLogoPreview(null);
    handleChange('logo', null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.success('Logo supprimé');
  };

  const currencies = [
    { value: 'DA', label: 'Dinar Algérien (DA)' },
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'USD', label: 'Dollar US ($)' }
  ];

  const languages = [
    { value: 'fr', label: 'Français' },
    { value: 'ar', label: 'العربية' },
    { value: 'en', label: 'English' }
  ];

  const timezones = [
    { value: 'Africa/Algiers', label: 'Alger (UTC+1)' },
    { value: 'Europe/Paris', label: 'Paris (UTC+1)' },
    { value: 'Europe/London', label: 'Londres (UTC+0)' }
  ];

  return (
    <div className={SETTINGS_PAGE_STACK}>
      <div className={SETTINGS_FORM_GRID}>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className={SETTINGS_FIELD}
        >
          <label className={SETTINGS_LABEL}>
            <BuildingOfficeIcon className="h-4 w-4 shrink-0" />
            Nom de l&apos;établissement
          </label>
          <input
            type="text"
            value={formData.gymName}
            onChange={(e) => handleChange('gymName', e.target.value)}
            className={SETTINGS_INPUT}
            placeholder="Ex. FORMAPRO — École de formation"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className={SETTINGS_FIELD}
        >
          <label className={SETTINGS_LABEL}>
            <MapPinIcon className="h-4 w-4 shrink-0" />
            Adresse
          </label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            className={SETTINGS_INPUT}
            placeholder="Adresse complète de l'établissement"
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className={SETTINGS_FIELD}>
          <label className={SETTINGS_LABEL}>
            <PhoneIcon className="h-4 w-4 shrink-0" />
            Téléphone
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            className={SETTINGS_INPUT}
            placeholder="+213 XXX XXX XXX"
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className={SETTINGS_FIELD}>
          <label className={SETTINGS_LABEL}>
            <EnvelopeIcon className="h-4 w-4 shrink-0" />
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className={SETTINGS_INPUT}
            placeholder="contact@formapro.dz"
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className={SETTINGS_FIELD}>
          <label className={SETTINGS_LABEL}>
            <GlobeAltIcon className="h-4 w-4 shrink-0" />
            Site web
          </label>
          <input
            type="url"
            value={formData.website}
            onChange={(e) => handleChange('website', e.target.value)}
            className={SETTINGS_INPUT}
            placeholder="https://votre-ecole.dz"
          />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className={SETTINGS_FIELD}>
          <label className={SETTINGS_LABEL}>
            <CurrencyDollarIcon className="h-4 w-4 shrink-0" />
            Devise
          </label>
          <select
            value={formData.currency}
            onChange={(e) => handleChange('currency', e.target.value)}
            className={SETTINGS_SELECT}
          >
            {currencies.map((currency) => (
              <option key={currency.value} value={currency.value}>
                {currency.label}
              </option>
            ))}
          </select>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className={SETTINGS_FIELD}>
          <label className={SETTINGS_LABEL}>
            <LanguageIcon className="h-4 w-4 shrink-0" />
            Langue par défaut
          </label>
          <select
            value={formData.language}
            onChange={(e) => handleChange('language', e.target.value)}
            className={SETTINGS_SELECT}
          >
            {languages.map((lang) => (
              <option key={lang.value} value={lang.value}>
                {lang.label}
              </option>
            ))}
          </select>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className={SETTINGS_FIELD}>
          <label className={SETTINGS_LABEL}>
            <ClockIcon className="h-4 w-4 shrink-0" />
            Fuseau horaire
          </label>
          <select
            value={formData.timezone}
            onChange={(e) => handleChange('timezone', e.target.value)}
            className={SETTINGS_SELECT}
          >
            {timezones.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className={clsx(SETTINGS_FIELD, 'lg:col-span-2')}
        >
          <label className={SETTINGS_LABEL}>
            <PhotoIcon className="h-4 w-4 shrink-0" />
            Logo de l&apos;établissement
          </label>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            {/* Upload Area */}
            <div className="flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
              />
              <label
                htmlFor="logo-upload"
                className="flex h-36 w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white transition-colors hover:border-forma-400 hover:bg-forma-50/50 dark:border-slate-600 dark:bg-slate-800/40 dark:hover:bg-slate-800/80"
              >
                <div className="text-center">
                  {uploadStatus === 'uploading' ? (
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/60"></div>
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">Téléchargement...</p>
                    </div>
                  ) : (
                    <>
                      <CloudArrowUpIcon className="h-8 w-8 text-slate-400 dark:text-slate-500 mx-auto" />
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">Cliquez pour télécharger</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500">PNG, JPG (max. 5MB)</p>
                    </>
                  )}
                </div>
              </label>
              
              {uploadStatus === 'error' && (
                <p className="mt-2 text-sm text-red-400">
                  Erreur: Fichier invalide ou trop volumineux
                </p>
              )}
              
              {uploadStatus === 'success' && (
                <p className="mt-2 text-sm text-green-400">
                  Logo téléchargé avec succès
                </p>
              )}
            </div>

            {/* Logo Preview */}
            {logoPreview && (
              <div className="relative">
                <div className="w-32 h-32 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-200 dark:border-slate-600 flex items-center justify-center overflow-hidden">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <button
                  onClick={handleLogoRemove}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-slate-900 dark:text-slate-50 rounded-full hover:bg-red-600 transition-colors"
                  title="Supprimer le logo"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Aperçu des paramètres */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className={SETTINGS_SECTION}
      >
        <h3 className="mb-4 text-base font-semibold text-slate-900 dark:text-slate-50">Aperçu</h3>
        <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
          <div>
            <span className="text-slate-600 dark:text-slate-400">Nom :</span>
            <span className="text-slate-900 dark:text-slate-50 ml-2">{formData.gymName || 'Non défini'}</span>
          </div>
          <div>
            <span className="text-slate-600 dark:text-slate-400">Devise :</span>
            <span className="text-slate-900 dark:text-slate-50 ml-2">{formData.currency}</span>
          </div>
          <div>
            <span className="text-slate-600 dark:text-slate-400">Langue :</span>
            <span className="text-slate-900 dark:text-slate-50 ml-2">
              {languages.find(l => l.value === formData.language)?.label}
            </span>
          </div>
          <div>
            <span className="text-slate-600 dark:text-slate-400">Fuseau horaire :</span>
            <span className="text-slate-900 dark:text-slate-50 ml-2">
              {timezones.find(t => t.value === formData.timezone)?.label}
            </span>
          </div>
          <div>
            <span className="text-slate-600 dark:text-slate-400">Logo :</span>
            <span className="text-slate-900 dark:text-slate-50 ml-2">
              {formData.logo ? 'Configuré' : 'Non défini'}
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default GeneralSettings; 